import { Texture } from '@pixi/core';
import { Container } from '@pixi/display';
import { Sprite } from '@pixi/sprite';
import { Text } from '@pixi/text';
import { Signal } from 'typed-signals';

export interface DebugAdjusterOptions
{
    increment?: number;
    min?: number;
    max?: number;
    roundToPoints?: number;
}

export class DebugAdjuster extends Container
{
    public label: Text;
    public increaseBtn: Sprite;
    public decreaseBtn: Sprite;
    public onUpdated = new Signal<(v: number) => void>();

    private _options: DebugAdjusterOptions;
    private _value!: number;

    constructor(options: DebugAdjusterOptions)
    {
        super();
        this._options = options;

        this.label = new Text('VALUE', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'center',
            fontSize: 35,
            lineJoin: 'round',
            stroke: 'black',
            strokeThickness: 2,
        });
        this.label.anchor.set(0.5);
        this.label.scale.set(0.75);
        this.addChild(this.label);

        this.increaseBtn = new Sprite(Texture.WHITE);
        this.increaseBtn.tint = 0x00FF00;
        this.increaseBtn.width = this.increaseBtn.height = 20;
        this.increaseBtn.anchor.set(0.5);
        this.addChild(this.increaseBtn);
        this.increaseBtn.interactive = this.increaseBtn.buttonMode = true;
        this.increaseBtn.x = 40;

        this.decreaseBtn = new Sprite(Texture.WHITE);
        this.decreaseBtn.tint = 0xFF0000;
        this.decreaseBtn.width = this.decreaseBtn.height = 20;
        this.decreaseBtn.anchor.set(0.5);
        this.addChild(this.decreaseBtn);
        this.decreaseBtn.interactive = this.decreaseBtn.buttonMode = true;
        this.decreaseBtn.x = -40;

        this.increaseBtn.on('pointertap', this._increase.bind(this));
        this.decreaseBtn.on('pointertap', this._decrease.bind(this));
    }

    private _increase(): void
    {
        this.value += this._options.increment ?? 0.1;
    }

    private _decrease(): void
    {
        this.value -= this._options.increment ?? 0.1;
    }

    get value(): number
    {
        return this._value;
    }

    set value(v: number)
    {
        this._value = Math.min(Math.max(this._options.min ?? 0, v), this._options.max ?? 1);

        if (this._options.roundToPoints)
        {
            const multiplier = Math.pow(10, this._options.roundToPoints);

            this._value = Math.round(this._value * multiplier) / multiplier;
        }

        this.label.text = this._value.toString();
        this.onUpdated.emit(this._value);
    }
}
